import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import styles from './LeaderMissions.module.css';
import { Editor } from '@tinymce/tinymce-react';
import { getTinymceInit } from '../shared/tinymceConfig';
import { useToast } from '../context/ToastContext';
import { getLevel } from '../utils/levels';
import LevelBadge from '../components/LevelBadge/LevelBadge';

interface DeliverableItem {
  code: string;
  name?: string;
  quantity?: number;
  unit?: string;
  notes?: string;
}

// Tooltip helper component
const FieldTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className={styles.tooltip} title={text}>ℹ️</span>
);

// Helper to get status color class
const getStatusClass = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open': return styles.statusOpen;
    case 'inprogress': return styles.statusInProgress;
    case 'pendingverification': return styles.statusPendingVerification;
    case 'closed': return styles.statusClosed;
    default: return '';
  }
};

// Helper to format time elapsed
const getTimeElapsed = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  return 'just now';
};

// Helper to format timestamp with timezone
const formatTimestamp = (dateString: string) => {
  const date = new Date(dateString);
  const timeStr = date.toLocaleString();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const elapsed = getTimeElapsed(dateString);
  return `${timeStr} (${timezone}) - ${elapsed}`;
};

interface MissionItem {
  id: number;
  title: string;
  objective?: string;
  briefing?: string;
  status: string; // Backend sends: Open, InProgress, PendingVerification, Closed, Archived
  createdBy?: string;
  deliverables?: DeliverableItem[];
  maxParticipants?: number;
  rewardPoints?: number;
  bonusPoints?: number;
  acceptances?: {
    id: number;
    memberUsername: string;
    status: string;
    acceptedAt: string;
    completedAt?: string;
  }[];
}

const LeaderMissions: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'create' | 'all' | 'metrics'>('create');
  const { showToast, showMissionCompleteToast, showLevelUpToast } = useToast();
  // simple filters for All Missions (placeholder)
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [textFilter, setTextFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState<string>('all');
  const [orgUsers, setOrgUsers] = useState<{id: number, username: string}[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [title, setTitle] = useState('');
  const [briefing, setBriefing] = useState('');
  // Template-driven fields
  type MissionTemplate = 'resource_gathering' | 'intel_gathering' | 'item_collection';
  const [selectedTemplate, setSelectedTemplate] = useState<MissionTemplate | null>(null);
  const [destination, setDestination] = useState('');
  const [deliverables, setDeliverables] = useState<DeliverableItem[]>([]);
  const [maxParticipants, setMaxParticipants] = useState<number | undefined>(undefined);
  const [completionTimeLimit, setCompletionTimeLimit] = useState<number | undefined>(undefined);
  const [timeLimitUnit, setTimeLimitUnit] = useState<'hours' | 'days' | 'indefinite'>('indefinite');
  const [bonusPoints, setBonusPoints] = useState<number>(0); // Bonus points on top of calculated points
  const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY || '';
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5069';

  // All missions view state
  const [missions, setMissions] = useState<MissionItem[]>([]);
  const [selectedMission, setSelectedMission] = useState<MissionItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');

  const fetchMissions = async () => {
    try {
      const res = await api.get('/missions/all');
      console.log('📋 Missions data received:', res.data);
      setMissions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch missions', err);
    }
  };

  const fetchOrgUsers = async () => {
    try {
      const res = await api.get('/auth/users');
      const mapped = (res.data || []).map((u: any) => ({ id: u.id, username: u.username }));
      setOrgUsers(mapped);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  // Load missions when switching to "All Missions" tab
  useEffect(() => {
    if (activeTab === 'all') {
      fetchMissions();
      fetchOrgUsers();
    }
  }, [activeTab]);

  // Calculate reward points based on mission type, deliverables, and time limit
  const calculateRewardPoints = (): number => {
    let basePoints = 0;
    let timeBonus = 0;

    // Calculate base points
    if (selectedTemplate === 'resource_gathering') {
      // 10-point base + 15 points per 25 SCU
      const totalSCU = deliverables.reduce((sum, d) => {
        const qty = d.quantity || 0;
        if (d.unit === 'uSCU') {
          return sum + (qty / 1000000); // Convert uSCU to SCU
        } else if (d.unit === 'SCU') {
          return sum + qty;
        }
        return sum;
      }, 0);
      
      basePoints = Math.max(10, 10 + Math.round((totalSCU / 25) * 15));
    } else if (selectedTemplate === 'item_collection') {
      basePoints = 20; // Flat rate
    } else if (selectedTemplate === 'intel_gathering') {
      basePoints = 0; // Intel only gets time bonus
    }

    // Calculate time bonus
    if (timeLimitUnit !== 'indefinite' && completionTimeLimit) {
      const hours = timeLimitUnit === 'days' ? completionTimeLimit * 24 : completionTimeLimit;
      if (hours >= 2 && hours <= 3) {
        timeBonus = 75;
      } else if (hours > 3 && hours <= 24) {
        timeBonus = 50;
      } else if (hours > 24) {
        timeBonus = 20;
      }
    }

    return basePoints + timeBonus;
  };

  const addDeliverable = () => {
    const defaultUnit = selectedTemplate === 'resource_gathering' ? 'SCU' : 'QTY';
    setDeliverables(prev => [...prev, { code: '', name: '', quantity: undefined, unit: defaultUnit, notes: '' }]);
  };

  const updateDeliverable = (index: number, field: keyof DeliverableItem, value: any) => {
    setDeliverables(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const removeDeliverable = (index: number) => {
    setDeliverables(prev => prev.filter((_, i) => i !== index));
  };

  const buildBriefing = (): string => {
    if (!selectedTemplate || selectedTemplate === 'intel_gathering') return briefing;
    
    const rewardPoints = calculateRewardPoints();
    const totalReward = rewardPoints + (bonusPoints || 0);
    const rewardText = totalReward > 0 
      ? `<p><strong>Reward:</strong> ${totalReward} organization points upon successful completion and verification${bonusPoints > 0 ? ` (${rewardPoints} base + ${bonusPoints} bonus)` : ''}.</p>` 
      : '';
    const timeLimitText = timeLimitUnit !== 'indefinite' && completionTimeLimit ? 
      `<p><strong>Time Limit:</strong> Complete within ${completionTimeLimit} ${timeLimitUnit === 'days' ? 'day(s)' : 'hour(s)'} of accepting the mission.</p>` : '';
    
    switch (selectedTemplate) {
      case 'resource_gathering': {
        const itemsList = deliverables.map(d => 
          `${d.quantity || '?'} ${d.unit || 'units'} of ${d.name || d.code || 'resource'}${d.notes ? ` (${d.notes})` : ''}`
        ).join(', ');
        return `<p>Mission: Collect and deliver resources to ${destination || 'designated location'}.</p>
<p><strong>Required Items:</strong> ${itemsList || 'None specified'}</p>
<p>Ensure all items are collected and delivered safely to the destination.</p>
${timeLimitText}
${rewardText}`;
      }
      case 'item_collection': {
        const itemsList = deliverables.map(d => 
          `${d.quantity || '?'} ${d.name || d.code || 'item'}${d.notes ? ` (${d.notes})` : ''}`
        ).join(', ');
        return `<p>Mission: Collect specific items and deliver to ${destination || 'designated location'}.</p>
<p><strong>Required Items:</strong> ${itemsList || 'None specified'}</p>
<p>Retrieve all listed items and transport them to the destination.</p>
${timeLimitText}
${rewardText}`;
      }
      default:
        return briefing;
    }
  };

  const buildObjective = (): string => {
    if (!selectedTemplate) return '';
    switch (selectedTemplate) {
      case 'resource_gathering': {
        const totalQty = deliverables.reduce((sum, d) => sum + (d.quantity || 0), 0);
        const units = deliverables.map(d => d.unit).filter(Boolean)[0] || 'units';
        return `Collect ${totalQty || 'specified'} ${units} of resources and deliver to ${destination || 'designated location'}`;
      }
      case 'intel_gathering':
        return `Gather operational intelligence within the AO and deliver report to ${destination || 'HQ'}`;
      case 'item_collection': {
        const items = deliverables.map(d => d.name || d.code).filter(Boolean);
        return `Collect ${items.length ? items.join(', ') : 'specified items'} and deliver to ${destination || 'designated drop-off'}`;
      }
      default:
        return '';
    }
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      showToast('Title is required', 'warning');
      return;
    }
    
    // Validate time limit minimum
    if (timeLimitUnit !== 'indefinite') {
      const hours = timeLimitUnit === 'days' ? (completionTimeLimit || 0) * 24 : (completionTimeLimit || 0);
      if (hours < 2) {
        showToast('Time limit must be at least 2 hours', 'warning');
        return;
      }
    }
    
    try {
      // Convert time limit to hours
      const timeLimitInHours = timeLimitUnit === 'indefinite' ? null : 
        timeLimitUnit === 'days' ? (completionTimeLimit || 0) * 24 : completionTimeLimit;
      
      const payload = {
        title,
        briefing: buildBriefing(),
        objective: buildObjective(),
        deliverables,
        missionType: selectedTemplate,
        completionTimeLimit: timeLimitInHours,
        destination,
        maxParticipants,
        bonusPoints: bonusPoints || 0 // Add bonus points to payload
      };
      const res = await api.post('/missions', payload);
      const creatorPoints = res.data?.creatorPointsAwarded || 10;
      showToast(`Mission created successfully! You earned ${creatorPoints} ORG Points for creating this mission.`, 'success', { duration: 5000, points: creatorPoints });
      setTitle(''); setBriefing(''); setSelectedTemplate(null); setDestination(''); 
      setDeliverables([]); setMaxParticipants(undefined); 
      setCompletionTimeLimit(undefined); setTimeLimitUnit('indefinite');
      await fetchMissions(); // Refresh the missions list
    } catch (err: any) {
      console.error('Create mission failed', err);
      showToast('Failed to create mission', 'error');
    }
  };

  const openViewModal = (mission: MissionItem) => {
    setSelectedMission(mission);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const openEditModal = (mission: MissionItem) => {
    setSelectedMission(mission);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const closeMissionModal = () => {
    setIsModalOpen(false);
    setSelectedMission(null);
  };

  const handleUpdateMission = async () => {
    if (!selectedMission) return;
    if (selectedMission.status === 'closed') {
      showToast('Closed missions cannot be edited.', 'warning');
      return;
    }
    try {
      await api.put(`/missions/${selectedMission.id}`, selectedMission);
      setMissions(prev => prev.map(m => m.id === selectedMission.id ? selectedMission : m));
      closeMissionModal();
      showToast('Mission updated successfully', 'success');
    } catch (err) {
      console.error('Update mission failed', err);
      showToast('Failed to update mission', 'error');
    }
  };

  // API currently has no DELETE for missions; offer a placeholder action.
  const handleCloseMission = async (missionId: number) => {
    // Find the mission to check for active acceptances
    const mission = missions.find(m => m.id === missionId);
    const hasActiveAcceptances = mission?.acceptances && mission.acceptances.length > 0;
    
    // Show stronger warning if there are active members
    let confirmMessage = 'Are you sure you want to close this mission?';
    if (hasActiveAcceptances && mission?.acceptances) {
      const memberCount = mission.acceptances.length;
      const memberNames = mission.acceptances.map(a => a.memberUsername).join(', ');
      confirmMessage = `⚠️ WARNING: This mission has ${memberCount} active member${memberCount > 1 ? 's' : ''} (${memberNames}).\n\nClosing this mission will affect their active work. Are you sure you want to close it?`;
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      // Prefer generic status endpoint for flexibility
      await api.patch(`/missions/${missionId}/status`, { status: 'Closed' });
      setMissions(prev => prev.map(m => m.id === missionId ? { ...m, status: 'Closed' } : m));
      showToast('Mission closed successfully', 'success');
    } catch (err) {
      console.error('Close mission failed', err);
      const anyErr = err as any;
      const serverMsg = anyErr?.response?.data?.detail || anyErr?.response?.data?.message || anyErr?.message || 'Failed to close mission';
      showToast(serverMsg, 'error');
    }
  };

  const handleVerifyCompletion = async (missionId: number) => {
    if (!window.confirm('Verify this mission as completed? This will award the member their reward points and close the mission.')) return;
    
    try {
      const response = await api.post(`/missions/${missionId}/verify-completion`);
      const data = response.data;
      
      await fetchMissions(); // Refresh to show updated status
      setIsModalOpen(false);
      
      // Show verifier's reward
      const verifierPoints = data.verifierPointsAwarded || 25;
      showToast(`Mission verified successfully! You earned ${verifierPoints} ORG Points for verifying this mission.`, 'success', { duration: 5000, points: verifierPoints });
    } catch (err) {
      console.error('Verify mission failed', err);
      const anyErr = err as any;
      const serverMsg = anyErr?.response?.data?.detail || anyErr?.response?.data?.message || anyErr?.message || 'Failed to verify mission';
      showToast(serverMsg, 'error');
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Manage Missions</h1>
          <p className={styles.subtitle}>Plan, publish, and track missions aligned with org workflows.</p>
        </div>

        <div className={styles.tabsBar}>
          <button className={`${styles.tabButton} ${activeTab === 'create' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('create')}>Create</button>
          <button className={`${styles.tabButton} ${activeTab === 'all' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('all')}>All Missions</button>
          <button className={`${styles.tabButton} ${activeTab === 'metrics' ? styles.tabButtonActive : ''}`} onClick={() => setActiveTab('metrics')}>Metrics</button>
        </div>

        {activeTab === 'create' && (
          <>
            {/* Template selection */}
            {!selectedTemplate && (
              <div className={styles.templates}>
                <h3 className={styles.sectionTitle}>Choose a Mission Template</h3>
                <p className={styles.sectionSubtitle}>Select a template to start configuring the mission.</p>
                <div className={styles.templateGrid}>
                  {([
                    { key: 'resource_gathering', label: 'Resource Gathering', desc: 'Collect commodities and resources measured in SCU' },
                    { key: 'intel_gathering', label: 'Intel Gathering', desc: 'Gather and report operational intelligence' },
                    { key: 'item_collection', label: 'Item Collection', desc: 'Collect items, equipment, or other non-commodity objects' },
                  ] as { key: MissionTemplate; label: string; desc: string }[]).map(t => (
                    <button key={t.key} className={styles.templateCard} onClick={() => setSelectedTemplate(t.key)}>
                      <span className={styles.templateTitle}>{t.label}</span>
                      <span className={styles.templateDesc}>{t.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Form renders after template selection */}
            {selectedTemplate && (
            <div className={styles.grid}>
          <div className={styles.backRow}>
            <button className={styles.buttonGhost} onClick={() => setSelectedTemplate(null)}>← Back to templates</button>
          </div>
          <label className={styles.label}>
            <span className={styles.labelWithTooltip}>
              Title
              <FieldTooltip text="Short, descriptive name for the mission (e.g., 'Commodity Collection – Laranite')" />
            </span>
            <input className={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Commodity Collection – Laranite" />
          </label>

          <label className={styles.label}>
            <span className={styles.labelWithTooltip}>
              Destination
              <FieldTooltip text="Where members should deliver items or complete objectives (e.g., 'Port Olisar – Hangar C')" />
            </span>
            <input className={styles.input} value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g., Port Olisar – Hangar C" />
          </label>

          {selectedTemplate === 'intel_gathering' && (
          <div className={styles.label}>
            <span className={styles.labelWithTooltip}>
              Briefing (details/instructions)
              <FieldTooltip text="Detailed instructions, context, and background information for members accepting the mission" />
            </span>
            <Editor
              apiKey={tinymceApiKey}
              value={briefing}
              onEditorChange={(val) => setBriefing(val)}
              init={getTinymceInit(apiBase)}
            />
          </div>
          )}

          {selectedTemplate !== 'intel_gathering' && (
          <div className={styles.label}>
            <span className={styles.labelWithTooltip}>
              Briefing (auto-generated)
              <FieldTooltip text="This briefing is automatically generated based on your deliverables and destination" />
            </span>
            <div className={styles.readonlyField} dangerouslySetInnerHTML={{ __html: buildBriefing() || '<em>Add deliverables and destination to generate briefing...</em>' }} />
          </div>
          )}

        {selectedTemplate !== 'intel_gathering' && (
        <div>
          <div className={styles.deliverablesHeader}>
            <span className={styles.labelWithTooltip}>
              <strong>Deliverables</strong>
              <FieldTooltip text="Items or resources to be collected. Each item can have a code (e.g., commodity code), name, quantity, and unit." />
            </span>
            <button className={styles.buttonGhost} onClick={addDeliverable}>Add Item</button>
          </div>
          {deliverables.map((d, i) => (
            <div key={i} className={styles.deliverableRowWithLabels}>
              <div className={styles.deliverableInputsTop}>
                <div className={styles.deliverableFieldWrapper}>
                  {i === 0 && (
                    <span className={styles.labelWithTooltip}>
                      Code
                      <FieldTooltip text="Item/commodity code or identifier" />
                    </span>
                  )}
                  <input className={styles.input} placeholder="Code" value={d.code} onChange={e => updateDeliverable(i, 'code', e.target.value)} />
                </div>
                <div className={styles.deliverableFieldWrapper}>
                  {i === 0 && (
                    <span className={styles.labelWithTooltip}>
                      Name
                      <FieldTooltip text="Display name for this item" />
                    </span>
                  )}
                  <input className={styles.input} placeholder="Name" value={d.name || ''} onChange={e => updateDeliverable(i, 'name', e.target.value)} />
                </div>
                <div className={styles.deliverableFieldWrapper}>
                  {i === 0 && (
                    <span className={styles.labelWithTooltip}>
                      Quantity
                      <FieldTooltip text="Amount to collect" />
                    </span>
                  )}
                  <input className={styles.numberInput} placeholder="Quantity" type="number" value={d.quantity ?? ''} onChange={e => updateDeliverable(i, 'quantity', e.target.value ? Number(e.target.value) : undefined)} />
                </div>
                <div className={styles.deliverableFieldWrapper}>
                  {i === 0 && (
                    <span className={styles.labelWithTooltip}>
                      Unit
                      <FieldTooltip text="Unit of measurement" />
                    </span>
                  )}
                  <select className={styles.select} value={d.unit || ''} onChange={e => updateDeliverable(i, 'unit', e.target.value)}>
                    {selectedTemplate === 'resource_gathering' ? [
                      <option key="uSCU" value="uSCU">uSCU</option>,
                      <option key="SCU" value="SCU">SCU</option>
                    ] : selectedTemplate === 'item_collection' ? [
                      <option key="QTY" value="QTY">QTY</option>
                    ] : null}
                  </select>
                </div>
                <div className={styles.deliverableFieldWrapper}>
                  {i === 0 && <span className={styles.deliverableEmptyLabel}></span>}
                  <button className={styles.buttonGhost} onClick={() => removeDeliverable(i)}>Remove</button>
                </div>
              </div>
              <div className={styles.deliverableFieldWrapper}>
                {i === 0 && (
                  <span className={styles.labelWithTooltip}>
                    Notes
                    <FieldTooltip text="Additional notes or specifications for this item" />
                  </span>
                )}
                <input className={styles.input} placeholder="Notes" value={d.notes || ''} onChange={e => updateDeliverable(i, 'notes', e.target.value)} />
              </div>
            </div>
          ))}
        </div>
        )}

        <label className={styles.label}>
          <span className={styles.labelWithTooltip}>
            Time Limit
            <FieldTooltip text="How long members have to complete the mission after accepting it. Shorter time limits award more bonus points." />
          </span>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select className={styles.select} value={timeLimitUnit} onChange={e => setTimeLimitUnit(e.target.value as any)} style={{ flex: '0 0 150px' }}>
              <option value="indefinite">No time limit</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
            {timeLimitUnit !== 'indefinite' && (
              <input 
                type="number" 
                className={styles.numberInput} 
                placeholder={timeLimitUnit === 'days' ? 'e.g., 3' : 'e.g., 48'} 
                value={completionTimeLimit ?? ''} 
                onChange={e => setCompletionTimeLimit(e.target.value ? Number(e.target.value) : undefined)}
                min={timeLimitUnit === 'hours' ? 2 : 1}
                style={{ flex: '1' }}
              />
            )}
          </div>
          {timeLimitUnit !== 'indefinite' && completionTimeLimit && (
            <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#888' }}>
              {timeLimitUnit === 'days' && completionTimeLimit * 24 < 2 && (
                <span style={{ color: '#ff4444' }}>⚠️ Minimum 2 hours required</span>
              )}
              {timeLimitUnit === 'hours' && completionTimeLimit < 2 && (
                <span style={{ color: '#ff4444' }}>⚠️ Minimum 2 hours required</span>
              )}
              {((timeLimitUnit === 'days' && completionTimeLimit * 24 >= 2) || (timeLimitUnit === 'hours' && completionTimeLimit >= 2)) && (
                <>
                  Time bonus: <strong>
                    {(() => {
                      const hours = timeLimitUnit === 'days' ? completionTimeLimit * 24 : completionTimeLimit;
                      if (hours >= 2 && hours <= 3) return '+75 points';
                      if (hours > 3 && hours <= 24) return '+50 points';
                      if (hours > 24) return '+20 points';
                      return '0 points';
                    })()}
                  </strong>
                </>
              )}
            </div>
          )}
        </label>

        <div className={styles.calculatedPointsDisplay}>
          <span className={styles.labelWithTooltip}>
            <strong>Calculated Reward Points</strong>
            <FieldTooltip text="Auto-calculated based on mission type, deliverables, and time limit" />
          </span>
          <div className={styles.pointsValue}>
            {calculateRewardPoints()} points
          </div>
          <div className={styles.pointsBreakdown}>
            {selectedTemplate === 'resource_gathering' && (
              <span>Resource: {(() => {
                const totalSCU = deliverables.reduce((sum, d) => {
                  const qty = d.quantity || 0;
                  return sum + (d.unit === 'uSCU' ? qty / 1000000 : d.unit === 'SCU' ? qty : 0);
                }, 0);
                return Math.max(10, 10 + Math.round((totalSCU / 25) * 15));
              })()} points</span>
            )}
            {selectedTemplate === 'item_collection' && <span>Base: 20 points</span>}
            {selectedTemplate === 'intel_gathering' && <span>Base: 0 points (time-only)</span>}
            {timeLimitUnit !== 'indefinite' && completionTimeLimit && (
              <span> + Time bonus: {(() => {
                const hours = timeLimitUnit === 'days' ? completionTimeLimit * 24 : completionTimeLimit;
                if (hours >= 2 && hours <= 3) return '75';
                if (hours > 3 && hours <= 24) return '50';
                if (hours > 24) return '20';
                return '0';
              })()} points</span>
            )}
          </div>
        </div>

        <label className={styles.label}>
          <span className={styles.labelWithTooltip}>
            <strong>Bonus Points (Optional)</strong>
            <FieldTooltip text="Add extra points on top of the automatically calculated reward. Use this to incentivize high-priority or difficult missions." />
          </span>
          <input
            type="number"
            min="0"
            step="5"
            value={bonusPoints || ''}
            onChange={e => setBonusPoints(parseInt(e.target.value) || 0)}
            placeholder="0"
            className={styles.input}
          />
          {bonusPoints > 0 && (
            <div className={styles.infoText} style={{ color: 'var(--accent-primary)' }}>
              Total reward: <strong>{calculateRewardPoints() + bonusPoints} points</strong> ({calculateRewardPoints()} calculated + {bonusPoints} bonus)
            </div>
          )}
        </label>

            <div className={styles.buttonRow}>
              <button className={styles.buttonPrimary} onClick={handleCreate}>Create Mission</button>
            </div>
            </div>
            )}
          </>
        )}

        {activeTab === 'all' && (
          <>
            <div className={styles.filterBar}>
              <select className={styles.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}>
                <option key="all" value="all">All Statuses</option>
                <option key="Open" value="Open">Open</option>
                <option key="InProgress" value="InProgress">In Progress</option>
                <option key="PendingVerification" value="PendingVerification">Pending Verification</option>
                <option key="Closed" value="Closed">Closed</option>
              </select>
              <select className={styles.select} value={creatorFilter} onChange={e => setCreatorFilter(e.target.value)}>
                <option key="all" value="all">All Creators</option>
                {orgUsers.map(u => (
                  <option key={u.id} value={u.username}>{u.username}</option>
                ))}
              </select>
              <input className={styles.searchInput} placeholder="Search title or objective..." value={textFilter} onChange={e => setTextFilter(e.target.value)} />
              <div className={styles.viewToggle}>
                <button className={`${styles.buttonGhost} ${viewMode === 'list' ? styles.active : ''}`} onClick={() => setViewMode('list')}>List</button>
                <button className={`${styles.buttonGhost} ${viewMode === 'card' ? styles.active : ''}`} onClick={() => setViewMode('card')}>Cards</button>
              </div>
              <button className={styles.buttonGhost} onClick={fetchMissions}>Refresh</button>
            </div>
            {viewMode === 'list' && (
            <div className={styles.list}>
              {missions
                .filter(m => statusFilter === 'all' ? true : m.status === statusFilter)
                .filter(m => creatorFilter === 'all' ? true : m.createdBy === creatorFilter)
                .filter(m => !textFilter || (m.title + ' ' + (m.objective || '')).toLowerCase().includes(textFilter.toLowerCase()))
                .map(m => (
                  <div key={m.id} className={styles.listItem}>
                    <div className={styles.listMain}>
                      <div className={styles.listTitle}>{m.title}</div>
                      <div className={styles.listSubtitle}>Objective: {m.objective || '—'}</div>
                      <div className={styles.listMeta}>Status: <span className={getStatusClass(m.status)}>{m.status === 'PendingVerification' ? 'Completed - PendingVerification' : m.status}</span></div>
                      {m.rewardPoints && (
                        <div className={styles.listMeta}>Reward: {(m.rewardPoints || 0) + (m.bonusPoints || 0)} ORG POINTS</div>
                      )}
                      {m.acceptances && m.acceptances.length > 0 && (
                        <div className={styles.listMeta}>
                          Accepted by: {m.acceptances.map(a => `${a.memberUsername} (${a.status})`).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className={styles.listActions}>
                      <button className={styles.buttonGhost} onClick={() => openViewModal(m)}>View</button>
                      {m.status.toLowerCase() === 'pendingverification' && (
                        <button className={styles.buttonSuccess} onClick={() => handleVerifyCompletion(m.id)}>✓ Verify</button>
                      )}
                      {m.status.toLowerCase() !== 'closed' && m.status.toLowerCase() !== 'pendingverification' && (
                        <button className={styles.buttonGhost} onClick={() => openEditModal(m)}>Edit</button>
                      )}
                      {m.status.toLowerCase() !== 'closed' && m.status.toLowerCase() !== 'pendingverification' && (
                        <button className={styles.buttonGhost} onClick={() => handleCloseMission(m.id)}>Close Mission</button>
                      )}
                    </div>
                  </div>
                ))}            </div>
            )}
            {viewMode === 'card' && (
            <div className={styles.cardGrid}>
              {missions
                .filter(m => statusFilter === 'all' ? true : m.status === statusFilter)
                .filter(m => creatorFilter === 'all' ? true : m.createdBy === creatorFilter)
                .filter(m => !textFilter || (m.title + ' ' + (m.objective || '')).toLowerCase().includes(textFilter.toLowerCase()))
                .map(m => (
                  <div key={m.id} className={styles.missionCard}>
                    <div className={styles.cardHeader}>
                      <h3 className={styles.cardTitle}>{m.title}</h3>
                      <span className={`${styles.statusBadge} ${styles[`status${m.status}`]}`}>{m.status === 'PendingVerification' ? 'Completed - PendingVerification' : m.status}</span>
                    </div>
                    <div className={styles.cardBody}>
                      <div className={styles.cardField}>
                        <strong>Objective:</strong>
                        <p>{m.objective || '—'}</p>
                      </div>
                      {m.rewardPoints && (
                        <p className={styles.cardMeta}>
                          <strong>Reward:</strong> {(m.rewardPoints || 0) + (m.bonusPoints || 0)} ORG POINTS
                        </p>
                      )}
                      {m.createdBy && (
                        <div className={styles.cardField}>
                          <strong>Created by:</strong> {m.createdBy}
                        </div>
                      )}
                      {m.acceptances && m.acceptances.length > 0 && (
                        <div className={styles.cardField}>
                          <strong>Accepted by:</strong>
                          <ul style={{ margin: '0.5rem 0', paddingLeft: '1.2rem' }}>
                            {m.acceptances.map(a => (
                              <li key={a.id}>
                                {a.memberUsername} - <span className={getStatusClass(a.status.toLowerCase())}>{a.status}</span>
                                {a.acceptedAt && (
                                  <div style={{ fontSize: '0.85em', color: '#aaa', marginTop: '0.25rem' }}>
                                    {formatTimestamp(a.acceptedAt)}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className={styles.cardActions}>
                      <button className={styles.buttonGhost} onClick={() => openViewModal(m)}>View</button>
                      {m.status.toLowerCase() === 'pendingverification' && (
                        <button className={styles.buttonSuccess} onClick={() => handleVerifyCompletion(m.id)}>✓ Verify</button>
                      )}
                      {m.status.toLowerCase() !== 'closed' && m.status.toLowerCase() !== 'pendingverification' && (
                        <button className={styles.buttonGhost} onClick={() => openEditModal(m)}>Edit</button>
                      )}
                      {m.status.toLowerCase() !== 'closed' && m.status.toLowerCase() !== 'pendingverification' && (
                        <button className={styles.buttonGhost} title="Close mission (pending API)" onClick={() => handleCloseMission(m.id)}>Close</button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            )}

            {isModalOpen && selectedMission && modalMode === 'edit' && (
              <div className={styles.modalOverlay} onClick={closeMissionModal}>
                <div className={styles.modal} onClick={e => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>Edit Mission</h3>
                    <button className={styles.modalClose} onClick={closeMissionModal}>×</button>
                  </div>
                  <div className={styles.modalBody}>
                    <label className={styles.label}>
                      Title
                      <input className={styles.input} value={selectedMission.title} onChange={e => setSelectedMission({ ...selectedMission, title: e.target.value })} />
                    </label>
                    <label className={styles.label}>
                      Objective
                      <input className={styles.input} value={selectedMission.objective || ''} onChange={e => setSelectedMission({ ...selectedMission, objective: e.target.value })} />
                    </label>
                    <div className={styles.label}>
                      Briefing
                      <Editor
                        apiKey={tinymceApiKey}
                        value={selectedMission.briefing || ''}
                        onEditorChange={(val) => setSelectedMission({ ...selectedMission, briefing: val })}
                        init={getTinymceInit(apiBase)}
                      />
                    </div>
                  </div>
                  <div className={styles.modalFooter}>
                    <button className={styles.buttonGhost} disabled={selectedMission.status === 'Closed'} onClick={handleUpdateMission}>Save Changes</button>
                    <button className={styles.buttonGhost} onClick={closeMissionModal}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {isModalOpen && selectedMission && modalMode === 'view' && (
              <div className={styles.modalOverlay} onClick={closeMissionModal}>
                <div className={styles.modal} onClick={e => e.stopPropagation()}>
                  <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>Mission Details</h3>
                    <button className={styles.modalClose} onClick={closeMissionModal}>×</button>
                  </div>
                  <div className={styles.modalBody}>
                    <div className={styles.label}>
                      <strong>Title</strong>
                      <div className={styles.readonlyField}>{selectedMission.title}</div>
                    </div>
                    <div className={styles.label}>
                      <strong>Objective</strong>
                      <div className={styles.readonlyField}>{selectedMission.objective || '—'}</div>
                    </div>
                    <div className={styles.label}>
                      <strong>Status</strong>
                      <div className={styles.readonlyField}>{selectedMission.status === 'PendingVerification' ? 'Completed - PendingVerification' : selectedMission.status}</div>
                    </div>
                    <div className={styles.label}>
                      <strong>Created By</strong>
                      <div className={styles.readonlyField}>{selectedMission.createdBy || '—'}</div>
                    </div>
                    {selectedMission.rewardPoints && (
                      <div className={styles.label}>
                        <strong>Reward</strong>
                        <div className={styles.readonlyField}>{selectedMission.rewardPoints} ORG POINTS</div>
                      </div>
                    )}
                    {selectedMission.acceptances && selectedMission.acceptances.length > 0 && (
                      <div className={styles.label}>
                        <strong>Accepted by</strong>
                        <div className={styles.readonlyField}>
                          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                            {selectedMission.acceptances.map(a => (
                              <li key={a.id} style={{ marginBottom: '0.5rem' }}>
                                <div>{a.memberUsername} - <span className={getStatusClass(a.status.toLowerCase())}>{a.status}</span></div>
                                {a.acceptedAt && (
                                  <div style={{ fontSize: '0.9em', color: '#aaa', marginTop: '0.25rem' }}>
                                    Accepted: {formatTimestamp(a.acceptedAt)}
                                  </div>
                                )}
                                {a.completedAt && (
                                  <div style={{ fontSize: '0.9em', color: '#aaa', marginTop: '0.25rem' }}>
                                    Completed: {formatTimestamp(a.completedAt)}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    <div className={styles.label}>
                      <strong>Briefing</strong>
                      <div className={styles.readonlyField} dangerouslySetInnerHTML={{ __html: selectedMission.briefing || '' }} />
                    </div>
                  </div>
                  <div className={styles.modalFooter}>
                    {selectedMission.status.toLowerCase() === 'pendingverification' && (
                      <button 
                        className={styles.buttonSuccess} 
                        onClick={() => handleVerifyCompletion(selectedMission.id)}
                      >
                        ✓ VERIFY COMPLETION
                      </button>
                    )}
                    {selectedMission.status.toLowerCase() !== 'closed' && selectedMission.status.toLowerCase() !== 'pendingverification' && (
                      <button className={styles.buttonGhost} onClick={() => { setModalMode('edit'); }}>Edit</button>
                    )}
                    {selectedMission.status.toLowerCase() !== 'closed' && (
                      <button className={styles.buttonGhost} onClick={() => handleCloseMission(selectedMission.id)}>Close Mission</button>
                    )}
                    <button className={styles.buttonGhost} onClick={closeMissionModal}>Close</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'metrics' && (
          <>
            <h2 className={styles.title}>Metrics & Reports</h2>
            <p style={{ color: 'var(--text-secondary)' }}>Coming soon: mission counts, completion rates, points issued, badges awarded, top contributors — similar to Medical SOS reports.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderMissions;
